import type { AxiosError } from "axios";
import { PromptValue, type RedirectRequest } from "@azure/msal-browser";
import http from "src/libs/http";
import MsalInstance from "src/services/authClient";

/**
 * The challenge's error_description when this session was signed out. Mirrors
 * the backend's RevokedSessionJwtBearerSetup.ErrorDescription.
 */
export const SIGNED_OUT_SESSION_DESCRIPTION = "The session has been signed out";

/**
 * The challenge's error_description when a newer sign-in elsewhere ended this
 * session under the concurrent session cap. Mirrors the backend's
 * RevokedSessionJwtBearerSetup.ReplacedErrorDescription.
 */
export const REPLACED_SESSION_DESCRIPTION =
	"The session was ended by a newer sign-in";

/**
 * How long after the user clicks Sign in a refusal of the session is taken as
 * "the session I just signed in to is in use elsewhere", and its place claimed
 * back (see claimSession). Long enough for the round trip through Entra, with
 * a password and MFA; short enough that a refusal hours later, when another
 * device really has signed in since, is taken at its word.
 */
export const SIGN_IN_CLAIM_WINDOW_MS = 5 * 60_000;

/** How long the claim waits for the API before giving up on it. */
export const SIGN_IN_API_TIMEOUT_MS = 5000;

/** Why the app signed the user out, for the sign-in screen to explain. */
export type SignOutReason = "replaced";

const SIGN_OUT_REASON_KEY = "mee.signOutReason";
const FRESH_SIGN_IN_KEY = "mee.freshSignIn";
const SIGNED_IN_AT_KEY = "mee.signedInAt";

function challengeOf(error: unknown): string | undefined {
	const response = (error as AxiosError | undefined)?.response;
	if (response?.status !== 401) return undefined;
	const challenge = response.headers?.["www-authenticate"];
	return typeof challenge === "string" ? challenge : undefined;
}

/**
 * True when the API refused the bearer token itself — expired, or from an
 * ended session (the backend's RevokedSessionJwtBearerSetup: signed out, or
 * replaced by a newer sign-in) — rather than an endpoint answering 401 for its
 * own reasons. Only a refused token comes back with the `invalid_token`
 * challenge; a plain `Results.Unauthorized()` (the chunked-upload ownership
 * check, for one) carries no challenge at all.
 */
export function isTokenRejection(error: unknown): boolean {
	return challengeOf(error)?.includes('error="invalid_token"') ?? false;
}

/** True when the refusal says a newer sign-in elsewhere ended this session. */
export function isSessionReplaced(error: unknown): boolean {
	return (
		isTokenRejection(error) &&
		(challengeOf(error)?.includes(REPLACED_SESSION_DESCRIPTION) ?? false)
	);
}

/**
 * True when the refusal says the API has ended this Entra session (signed
 * out, or replaced), as opposed to a token that merely expired.
 */
export function isSessionEnded(error: unknown): boolean {
	if (!isTokenRejection(error)) return false;
	const challenge = challengeOf(error) ?? "";
	return (
		challenge.includes(SIGNED_OUT_SESSION_DESCRIPTION) ||
		challenge.includes(REPLACED_SESSION_DESCRIPTION)
	);
}

// Session storage: it goes with the tab, and survives a redirect round trip
// in it. Storage can be unavailable (a locked-down browser); then the courtesy
// it carries is lost, never the sign-out itself.
function store(key: string, value: string): void {
	try {
		window.sessionStorage.setItem(key, value);
	} catch {
		// See above.
	}
}

function read(key: string): string | null {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
}

function forget(key: string): void {
	try {
		window.sessionStorage.removeItem(key);
	} catch {
		// Nothing was stored, then.
	}
}

/** The reason the app last signed this tab out, if it recorded one. */
export function readSignOutReason(): SignOutReason | null {
	return read(SIGN_OUT_REASON_KEY) === "replaced" ? "replaced" : null;
}

/**
 * Forgets the reason, once the user signs in again. Separate from the read,
 * which the sign-in screen does on every mount, so a remount can't lose the
 * explanation before the user has read it.
 */
export function clearSignOutReason(): void {
	forget(SIGN_OUT_REASON_KEY);
}

/**
 * Makes this tab's next sign-in a fresh one (`prompt=login`) rather than a
 * silent one, for a session no sign-in can bring back: a signed-out one.
 * Silently, Entra would hand back the browser's existing session, whose id is
 * the ended one: it is still alive when Mee signed out locally, or when the
 * user left Entra's own sign-out page without picking an account, and the app
 * would then be refused and signed out again on every attempt.
 *
 * A replaced session needs none of this. The next sign-in lands back in the
 * same session, and claims its place back (see claimSession). A fresh sign-in
 * would only start one more session in the browser, for that tab alone.
 */
export function requireFreshSignIn(): void {
	store(FRESH_SIGN_IN_KEY, "1");
}

/**
 * The request for the Sign in button: a fresh sign-in if this tab's last
 * session can't be brought back (see requireFreshSignIn), MSAL's default
 * otherwise. Consumes the flag and the sign-out reason, since the user is now
 * signing in, and notes when, so that a refusal on the way back is claimed
 * rather than obeyed.
 */
export function takeSignInRequest(): RedirectRequest | undefined {
	const fresh = read(FRESH_SIGN_IN_KEY) === "1";
	forget(FRESH_SIGN_IN_KEY);
	clearSignOutReason();
	store(SIGNED_IN_AT_KEY, String(Date.now()));
	return fresh ? { scopes: [], prompt: PromptValue.LOGIN } : undefined;
}

function signedInJustNow(): boolean {
	const at = Number(read(SIGNED_IN_AT_KEY));
	return at > 0 && Date.now() - at < SIGN_IN_CLAIM_WINDOW_MS;
}

/**
 * Wins the session's place back under the concurrent session cap, as the
 * user's newest sign-in: `POST /auth/sign-in/`, with the refused request's own
 * token. That is the one route (with sign-out) the API serves to a session it
 * has replaced. Entra hands a browser its own session back on every sign-in,
 * so this is how signing in again on a displaced browser gets back in, rather
 * than being refused for good.
 *
 * Plain axios, not cmsClient, whose interceptor would hand a refusal straight
 * back here. Resolves to the failure, or undefined on success.
 */
async function claimSession(refused: AxiosError): Promise<unknown> {
	const authorization = refused.config?.headers?.Authorization;
	if (typeof authorization !== "string") return refused;
	try {
		await http.post("/auth/sign-in/", undefined, {
			baseURL: window._env_.REACT_APP_RETAILCMS_BASEURL,
			headers: { Authorization: authorization },
			timeout: SIGN_IN_API_TIMEOUT_MS
		});
		return undefined;
	} catch (error) {
		return error;
	}
}

let claiming: Promise<void> | null = null;

/**
 * Claims once, however many requests were refused together; the others wait
 * on the outcome. A claim that wins reloads the page, so everything is
 * fetched again in the restored session. One that loses ends the session the
 * ordinary way, by the claim's own refusal if it was one.
 */
function claimOnce(refused: AxiosError): Promise<void> {
	claiming ??= claimSession(refused)
		.then((failure) => {
			if (failure === undefined) {
				window.location.reload();
				return;
			}
			return endSession(isTokenRejection(failure) ? failure : refused);
		})
		.finally(() => {
			claiming = null;
		});
	return claiming;
}

let signingOut = false;

/**
 * Signs out once, however many callers ask at the same time. Released again if
 * MSAL refuses (another interaction in progress), so a later request can retry.
 */
function signOutOnce(signOut: () => Promise<void>): Promise<void> {
	if (signingOut) return Promise.resolve();
	signingOut = true;
	return signOut().catch(() => {
		signingOut = false;
	});
}

/** Ends the Entra session too, via Entra's own sign-out page. */
export function signOutOfEntra(): Promise<void> {
	return signOutOnce(() => MsalInstance.logoutRedirect());
}

/**
 * Signs out of Mee only: clears MSAL's cache for this app and shows the
 * sign-in screen, without sending the user to Entra's sign-out page (an
 * account picker that does nothing until they choose, which is confusing
 * straight after being signed out by someone else's sign-in). The browser's
 * Entra session stays as it was, for the other Microsoft apps that use it.
 *
 * `clearCache` rather than `logoutRedirect` with its navigation suppressed:
 * that one marks an interaction in progress and fetches Entra's metadata
 * first, and if the fetch fails the mark stays, refusing the next sign-in.
 * `clearCache` needs no network, but tells msal-react nothing, so the page
 * reloads to come back up signed out.
 */
function signOutOfMee(): Promise<void> {
	return signOutOnce(async () => {
		await MsalInstance.clearCache();
		// MSAL logs and swallows a failure to clear. Reloading with the account
		// still cached would be refused again, and reload again, for ever: fall
		// back to Entra's sign-out instead.
		if (MsalInstance.getAllAccounts().length > 0) {
			await MsalInstance.logoutRedirect();
			return;
		}
		window.location.reload();
	});
}

/**
 * An ended session the app accepts as ended: sign out of Mee only, and leave
 * the sign-in screen what it needs to know.
 * - Replaced by a newer sign-in: say so. The next sign-in lands back in the
 *   session and claims its place back.
 * - Signed out: make the next sign-in a fresh one, so it can't land back in
 *   the ended session.
 */
function endSession(error: unknown): Promise<void> {
	if (isSessionReplaced(error)) store(SIGN_OUT_REASON_KEY, "replaced");
	else requireFreshSignIn();
	return signOutOfMee();
}

/**
 * Response-interceptor hook: when the API refuses the token, don't fetch
 * another, since a token from the same Entra session would carry the same
 * refused session id. Instead:
 * - A session replaced by a newer sign-in, refused on the way back from a
 *   Sign in the user clicked moments ago: claim its place back (claimSession)
 *   and reload. The user asked for this device.
 * - Any other ended session: sign out of Mee only (endSession).
 * - Anything else (an expired token): sign out fully, through Entra.
 * Skipped in the mobile app's web view, whose tokens and sign-in belong to the
 * app, and while a claim is deciding what becomes of the session.
 */
export function handleTokenRejection(error: unknown): void {
	if (!isTokenRejection(error) || window.ReactNativeWebView || claiming) return;
	if (!isSessionEnded(error)) {
		void signOutOfEntra();
		return;
	}
	if (isSessionReplaced(error) && signedInJustNow()) {
		forget(SIGNED_IN_AT_KEY);
		void claimOnce(error as AxiosError);
		return;
	}
	void endSession(error);
}
